import {
  Box,
  Card,
  List,
  ListItem,
  Typography,
  Checkbox,
  Tooltip,
  checkboxClasses,
  ListSubheader,
} from '@mui/material';
import PropTypes from 'prop-types';
import { IconButton } from '@mui/material';
import MergeIcon from '@mui/icons-material/Merge';
import AdminListFuzzySearch from './AdminListFuzzySearch';
import { Fragment, useState, useEffect, useMemo } from 'react';
import { typeCategoriesImportantForAnalytics, typesImportantForAnalytics } from '../../shared/constants/constants';
import EnumListTypeItem from './EnumListTypeItem';
import { adminCardSx, adminListScrollSx, adminSearchActionRowSx } from './adminStyles';

function EnumTypesListComponent({
  targetMergingItem,
  enumItems,
  addButton,
  setEnumModalOpen,
  setIsEditing,
  setSelectedEnum,
  isMerging,
  setIsMerging,
  setMergingItems,
  whatIsBeingMergedForTypes,
  setWhatIsBeingMergedForTypes,
}) {
  const [searchResults, setSearchResults] = useState(enumItems);
  const [categorizedSearchResults, setCategorizedSearchResults] = useState({});
  const [selectedItemKeys, setSelectedItemKeys] = useState(new Set());
  const additionalFuseOptionKeys = useMemo(() => ['type'], []);
  const getTypeKey = (item) => `type:${item?._id ?? item?.name ?? ''}`;
  const getCategoryKey = (category) => `category:${category}`;

  const handleCheckboxChange = (event, enumItem) => {
    const itemKey = typeof enumItem === 'string' ? getCategoryKey(enumItem) : getTypeKey(enumItem);
    const updatedSelectedItemKeys = new Set(selectedItemKeys);

    if (event.target.checked) {
      updatedSelectedItemKeys.add(itemKey);
    } else {
      updatedSelectedItemKeys.delete(itemKey);
    }

    setSelectedItemKeys(updatedSelectedItemKeys);

    if (whatIsBeingMergedForTypes === 'Types') {
      const nextSelectedTypes = enumItems.filter((item) => updatedSelectedItemKeys.has(getTypeKey(item)));
      setMergingItems(nextSelectedTypes);
    } else {
      const nextSelectedCategories = Object.keys(categorizedSearchResults).filter((category) =>
        updatedSelectedItemKeys.has(getCategoryKey(category))
      );
      setMergingItems(nextSelectedCategories);
    }
  };

  useEffect(() => {
    const searchResultsMappedByCategory = {};
    searchResults?.forEach((result) => {
      if (result['type'] in searchResultsMappedByCategory) {
        searchResultsMappedByCategory[result['type']][result.archived ? 'archived' : 'unarchived'].push(result);
      } else {
        searchResultsMappedByCategory[result['type']] = { archived: [], unarchived: [] };
        searchResultsMappedByCategory[result['type']][result.archived ? 'archived' : 'unarchived'].push(result);
      }
    });

    setCategorizedSearchResults(searchResultsMappedByCategory);
  }, [searchResults]);

  useEffect(() => {
    if (!isMerging) {
      setSelectedItemKeys(new Set());
      setMergingItems([]);
    }
  }, [isMerging, setMergingItems]);

  useEffect(() => {
    setSelectedItemKeys(new Set());
    setMergingItems([]);
  }, [whatIsBeingMergedForTypes, targetMergingItem, setMergingItems]);

  return (
    <Box sx={{ width: '100%' }}>
      <Card sx={adminCardSx}>
        <List
          sx={{
            ...adminListScrollSx,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'flex-start',
            padding: '0',
            margin: '0',
            width: '100%',
          }}
          disablePadding={true}
          padding={0}
        >
          <ListItem key={'searchbar'} sx={adminSearchActionRowSx}>
            <AdminListFuzzySearch
              items={enumItems}
              setSearchResults={setSearchResults}
              additionalFuseOptionKeys={additionalFuseOptionKeys}
              textFieldPlaceholder={'Search Names/Categories...'}
            />
            {!isMerging && addButton()}
          </ListItem>

          {categorizedSearchResults &&
            Object.keys(categorizedSearchResults)
              .sort((a, b) => {
                const categoryNameA = a.toLowerCase();
                const categoryNameB = b.toLowerCase();
                if (categoryNameA < categoryNameB) return -1;
                if (categoryNameA > categoryNameB) return 1;
                return 0;
              })
              .map((category, i) => {
                return (
                  <Fragment key={`category-header-${category}-${i}`}>
                    <ListItem
                      sx={{
                        alignContent: 'space-between',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        backgroundColor: (theme) => theme.palette.primary.main,
                        color: (theme) => theme.palette.primary.contrastText,
                      }}
                    >
                      {typeCategoriesImportantForAnalytics.includes(category) ? (
                        <Tooltip
                          title="This type category is important for analytics. No editing restrictions are in place, but please be advised when moving types in and out of this category."
                          placement="bottom-start"
                        >
                          <Typography sx={{ color: 'yellow' }}>{category}</Typography>
                        </Tooltip>
                      ) : (
                        <Typography>{category}</Typography>
                      )}

                      {isMerging ? (
                        whatIsBeingMergedForTypes === 'Type Categories' && (
                          <Checkbox
                            sx={{
                              [`&, &.${checkboxClasses.checked}`]: {
                                color: 'white',
                              },
                            }}
                            checked={selectedItemKeys.has(getCategoryKey(category))}
                            onChange={(event) => handleCheckboxChange(event, category)}
                            // targetMergingItem here is a string of the category name
                            disabled={targetMergingItem && category === targetMergingItem}
                          />
                        )
                      ) : (
                        <Tooltip title="Merge Type Categories">
                          <IconButton
                            sx={{ color: 'white' }}
                            size="small"
                            onClick={() => {
                              setSelectedEnum(category);
                              setWhatIsBeingMergedForTypes('Type Categories');
                              setIsMerging(true);
                            }}
                          >
                            <MergeIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </ListItem>

                    {categorizedSearchResults[category]['unarchived'].map((enumItem, i) => {
                      return (
                        <EnumListTypeItem
                          targetMergingItem={targetMergingItem}
                          key={enumItem._id || `unarchived-${category}-${i}`}
                          enumItem={enumItem}
                          isMerging={isMerging}
                          isSelected={selectedItemKeys.has(getTypeKey(enumItem))}
                          handleCheckboxChange={handleCheckboxChange}
                          setIsEditing={setIsEditing}
                          setSelectedEnum={setSelectedEnum}
                          setEnumModalOpen={setEnumModalOpen}
                          setIsMerging={setIsMerging}
                          typesImportantForAnalytics={typesImportantForAnalytics}
                          whatIsBeingMergedForTypes={whatIsBeingMergedForTypes}
                          setWhatIsBeingMergedForTypes={setWhatIsBeingMergedForTypes}
                        />
                      );
                    })}

                    {categorizedSearchResults[category]['archived'].length > 0 && (
                      <ListSubheader
                        sx={{
                          backgroundColor: (theme) => theme.palette.whiteScaleLighter.main,
                          color: 'text.secondary',
                          width: 'calc(100% - 1.5rem)',
                          borderRadius: '8px',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          mt: 1,
                          mb: 0.75,
                          mx: 0.75,
                          py: 0.35,
                        }}
                      >
                        Archived
                      </ListSubheader>
                    )}

                    {categorizedSearchResults[category]['archived'].map((enumItem, i) => {
                      return (
                        <EnumListTypeItem
                          targetMergingItem={targetMergingItem}
                          key={enumItem._id || `archived-${category}-${i}`}
                          enumItem={enumItem}
                          isMerging={isMerging}
                          isSelected={selectedItemKeys.has(getTypeKey(enumItem))}
                          handleCheckboxChange={handleCheckboxChange}
                          setIsEditing={setIsEditing}
                          setSelectedEnum={setSelectedEnum}
                          setEnumModalOpen={setEnumModalOpen}
                          setIsMerging={setIsMerging}
                          isArchivedItem={true}
                          typesImportantForAnalytics={typesImportantForAnalytics}
                          whatIsBeingMergedForTypes={whatIsBeingMergedForTypes}
                          setWhatIsBeingMergedForTypes={setWhatIsBeingMergedForTypes}
                        />
                      );
                    })}
                  </Fragment>
                );
              })}
        </List>
      </Card>
    </Box>
  );
}

EnumTypesListComponent.propTypes = {
  targetMergingItem: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  enumItems: PropTypes.array.isRequired,
  addButton: PropTypes.func.isRequired,
  setEnumModalOpen: PropTypes.func.isRequired,
  setIsEditing: PropTypes.func.isRequired,
  setSelectedEnum: PropTypes.func.isRequired,
  isMerging: PropTypes.bool.isRequired,
  setIsMerging: PropTypes.func.isRequired,
  setMergingItems: PropTypes.func.isRequired,
  whatIsBeingMergedForTypes: PropTypes.string.isRequired,
  setWhatIsBeingMergedForTypes: PropTypes.func.isRequired,
};

export default EnumTypesListComponent;
